/**
 * Our own PDF viewer, used by `DisplayDocument` for `application/pdf` sources.
 *
 * The browser's built-in viewer (a bare `<iframe src={pdfUrl}>`) gives us no
 * page state and no text, so the avatar could neither turn pages nor know what
 * the learner is looking at. pdf.js renders page-by-page on a canvas, which
 * makes both readable and controllable through the imperative handle below.
 *
 * Awareness is pull-only: nothing is pushed into the conversation when the
 * learner turns a page. Instead we remember that they moved, and the next tool
 * result carries that fact so the avatar can correct itself.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ChevronLeft, ChevronRight, ExternalLink, Loader2 } from "lucide-react";
import * as pdfjs from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { Button } from "@/components/ui/button";
import { createLogger } from "@/lib/frontend-logger";
import { ensurePdfWorker, pdfWorkerUrl as workerUrl, type PdfWorkerInfo } from "./pdfWorker";

const logger = createLogger("session.PdfDocument");

/** Where a failure happened, so a broken environment is identifiable from logs. */
export type PdfFailureStage = "worker" | "load" | "render";

export type PdfFailure = {
  stage: PdfFailureStage;
  /** Error name/message, never the document contents. */
  detail: string;
  /** Absolute URL of the bundled pdf.js worker in this build. */
  workerUrl: string;
  /** How the worker was loaded and what the host served, when known. */
  worker?: PdfWorkerInfo;
};



/** Where the viewer landed, plus whether the learner had moved it themselves. */
export type PdfPageState = {
  page: number;
  total: number;
  /** True when the learner turned pages since the last avatar-driven action. */
  learnerMoved: boolean;
};

export type PdfDocumentHandle = {
  /** 1-based page currently rendered. */
  currentPage(): number;
  pageCount(): number;
  /** Clamps to the document's range and returns where it landed. */
  goToPage(page: number): PdfPageState;
  /** Extracted text of the page currently on screen. */
  readCurrentPage(): Promise<PdfPageState & { text: string }>;
};

export const PdfDocument = forwardRef<
  PdfDocumentHandle,
  {
    /** URL pdf.js reads the bytes from (proxied through our backend). */
    url: string;
    /** Original file URL, used only for the "open in a new tab" link. */
    openUrl?: string;
    className?: string;
    onFailure?: (failure: PdfFailure) => void;
  }
>(function PdfDocument({ url, openUrl, className, onFailure }, ref) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const docRef = useRef<PDFDocumentProxy | null>(null);
  const pageRef = useRef(1);
  // The in-flight pdf.js render. Cancelled before starting another one: two
  // concurrent renders on the same canvas reject, which used to land in the
  // failure branch and leave the spinner up over an already-painted page.
  const renderTaskRef = useRef<{ cancel(): void } | null>(null);
  // True once any page has been painted — after that a re-render (a pane resize
  // when the split screen opens) must not blank the slide behind a spinner.
  const paintedRef = useRef(false);
  // Set when the learner navigates through the UI; cleared once reported to the
  // avatar through a tool result, or when the avatar drives the page itself.
  const learnerMovedRef = useRef(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  // Pane size drives fit-to-screen: the page is scaled so it fits on both axes.
  const [pane, setPane] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [pageInput, setPageInput] = useState<string | null>(null);

  // The failure callback is usually an inline arrow in the parent, so its
  // identity changes on every parent render. Kept in a ref and out of the effect
  // deps: as a dependency it re-ran the loader on every session re-render, which
  // tore the document down and put the spinner back over an already-drawn page.
  const onFailureRef = useRef(onFailure);
  onFailureRef.current = onFailure;

  // Keep a ref in step with state so the imperative handle (called from a D-ID
  // tool callback, outside React's render cycle) always reads the live page.
  const setCurrent = useCallback((n: number) => {
    pageRef.current = n;
    setPage(n);
  }, []);

  // What the host served for the worker asset, attached to any failure report.
  const workerInfoRef = useRef<PdfWorkerInfo | undefined>(undefined);

  const reportFailure = useCallback((stage: PdfFailureStage, e: unknown) => {
    const err = e as { name?: string; message?: string } | undefined;
    const detail = [err?.name, err?.message].filter(Boolean).join(": ") || String(e);
    const worker = workerInfoRef.current;
    // pdf.js reports a worker that won't start as a generic document failure
    // ("Setting up fake worker failed"). Re-label it: the environment couldn't
    // start the reader, the document itself was never the problem.
    const realStage: PdfFailureStage =
      stage === "load" && /fake worker|worker/i.test(detail) ? "worker" : stage;
    // Logged (not just surfaced) so a self-hosted environment we cannot sign
    // into still tells us which stage broke.
    logger.error("pdf viewer failed", {
      stage: realStage,
      detail,
      workerUrl,
      workerMode: worker?.mode,
      workerStatus: worker?.status,
      workerContentType: worker?.contentType,
    });
    onFailureRef.current?.({ stage: realStage, detail, workerUrl, worker });
  }, []);

  useEffect(() => {
    let cancelled = false;
    paintedRef.current = false;
    setLoading(true);

    let task: { promise: Promise<PDFDocumentProxy>; destroy(): Promise<void> } | null = null;

    // The worker is fetched by us and run from a Blob, so a host that serves
    // `.mjs` with the wrong content type can no longer break the reader.
    void ensurePdfWorker()
      .then((info) => {
        workerInfoRef.current = info;
        if (cancelled) return;
        task = pdfjs.getDocument({ url, withCredentials: false });
        return task.promise.then((doc) => {
          if (cancelled) return;
          docRef.current = doc;
          setTotal(doc.numPages);
          setCurrent(1);
        });
      })
      .catch((e: unknown) => {
        if (!cancelled) reportFailure("load", e);
      });

    return () => {
      cancelled = true;
      void task?.destroy();
      docRef.current = null;
    };
  }, [url, setCurrent, reportFailure]);




  // Track the pane size so fit-to-screen survives layout changes (the
  // split-screen presentation and the bubble avatar both resize this pane).
  // Identical sizes are dropped so opening the panel doesn't queue a string of
  // redundant re-renders.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = (w: number, h: number) =>
      setPane((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    measure(Math.round(el.clientWidth), Math.round(el.clientHeight));
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      measure(
        Math.round(rect?.width ?? el.clientWidth),
        Math.round(rect?.height ?? el.clientHeight),
      );
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Render the active page, scaled to fit inside the pane on both axes so the
  // whole slide is always visible without scrolling.
  useEffect(() => {
    const doc = docRef.current;
    const canvas = canvasRef.current;
    if (!doc || !canvas || !total) return;
    let cancelled = false;
    // Only the very first paint hides the slide behind a spinner; re-renders
    // (resize, page turn) keep the current pixels until the new ones land.
    if (!paintedRef.current) setLoading(true);
    doc
      .getPage(page)
      .then(async (p) => {
        if (cancelled) return;
        const paneWidth = pane.w || wrapRef.current?.clientWidth || 800;
        const paneHeight = pane.h || wrapRef.current?.clientHeight || 600;
        const base = p.getViewport({ scale: 1 });
        const pad = 32;
        const scale = Math.max(
          0.2,
          Math.min(
            (paneWidth - pad) / base.width,
            (paneHeight - pad) / base.height,
          ),
        );
        const dpr = window.devicePixelRatio || 1;
        const viewport = p.getViewport({ scale: scale * dpr });
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${viewport.width / dpr}px`;
        canvas.style.height = `${viewport.height / dpr}px`;
        // One render at a time on this canvas — a second concurrent one rejects.
        renderTaskRef.current?.cancel();
        const task = p.render({ canvas, canvasContext: ctx, viewport });
        renderTaskRef.current = task;
        await task.promise;
        renderTaskRef.current = null;
        paintedRef.current = true;
        setLoading(false);
      })
      .catch((e: unknown) => {
        // A cancelled render is expected (resize / fast paging) — not a failure.
        const name = (e as { name?: string })?.name;
        if (cancelled || name === "RenderingCancelledException") return;
        setLoading(false);
        reportFailure("render", e);
      });
    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
      renderTaskRef.current = null;
    };
  }, [page, total, pane.w, pane.h, reportFailure]);





  const go = useCallback(
    (next: number): PdfPageState => {
      const max = docRef.current?.numPages ?? total ?? 1;
      const clamped = Math.min(Math.max(Math.round(next) || 1, 1), Math.max(max, 1));
      setCurrent(clamped);
      return { page: clamped, total: max, learnerMoved: learnerMovedRef.current };
    },
    [total, setCurrent],
  );

  /** Navigation coming from the learner: remember it for the next tool result. */
  const goManual = useCallback(
    (next: number) => {
      const before = pageRef.current;
      const at = go(next);
      if (at.page !== before) learnerMovedRef.current = true;
    },
    [go],
  );

  // Keyboard paging while the viewer has focus.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "PageDown") {
        e.preventDefault();
        goManual(pageRef.current + 1);
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        goManual(pageRef.current - 1);
      }
    };
    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, [goManual]);

  useImperativeHandle(
    ref,
    () => ({
      currentPage: () => pageRef.current,
      pageCount: () => docRef.current?.numPages ?? 0,
      goToPage: (n: number) => {
        const at = go(n);
        // The avatar now knows where the document stands.
        learnerMovedRef.current = false;
        return at;
      },
      readCurrentPage: async () => {
        const doc = docRef.current;
        const current = pageRef.current;
        const learnerMoved = learnerMovedRef.current;
        learnerMovedRef.current = false;
        if (!doc) return { page: current, total: 0, text: "", learnerMoved };
        const p = await doc.getPage(current);
        const content = await p.getTextContent();
        const text = content.items
          .map((i) => ("str" in i ? i.str : ""))
          .join(" ")
          .replace(/\s+/g, " ")
          .trim()
          .slice(0, 4000);
        return { page: current, total: doc.numPages, text, learnerMoved };
      },
    }),
    [go],
  );

  const commitPageInput = () => {
    const n = Number(pageInput);
    setPageInput(null);
    if (Number.isFinite(n) && n >= 1) goManual(n);
  };

  return (
    <div className={"flex h-full w-full flex-col bg-muted/40 " + (className ?? "")}>
      {/* Full-width top bar: nothing overlays the slide itself. */}
      <div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-border bg-background/95 px-3 backdrop-blur">
        <Button
          size="sm"
          variant="ghost"
          className="h-9 gap-1.5 px-3"
          disabled={page <= 1}
          onClick={() => goManual(page - 1)}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="hidden sm:inline text-sm font-medium">Prev</span>
        </Button>

        {pageInput === null ? (
          <button
            type="button"
            onClick={() => setPageInput(String(page))}
            title="Go to page"
            className="min-w-[4.5rem] rounded-md px-2 py-1 text-center text-sm font-medium tabular-nums text-foreground hover:bg-muted"
          >
            {page} / {total || "…"}
          </button>
        ) : (
          <input
            autoFocus
            type="number"
            min={1}
            max={total || undefined}
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onBlur={commitPageInput}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitPageInput();
              if (e.key === "Escape") setPageInput(null);
            }}
            aria-label="Page number"
            className="w-[4.5rem] rounded-md border border-input bg-background px-2 py-1 text-center text-sm tabular-nums outline-none focus:ring-2 focus:ring-ring"
          />
        )}

        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-9 gap-1.5 px-3"
            disabled={!total || page >= total}
            onClick={() => goManual(page + 1)}
            aria-label="Next page"
          >
            <span className="hidden sm:inline text-sm font-medium">Next</span>
            <ChevronRight className="h-4 w-4" />
          </Button>

          <span className="mx-1 h-5 w-px bg-border" aria-hidden />

          <Button
            asChild
            size="icon"
            variant="ghost"
            className="h-9 w-9"
            title="Open in a new tab"
          >
            <a
              href={openUrl ?? url}
              target="_blank"
              rel="noreferrer"
              aria-label="Open in a new tab"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        </div>
      </div>

      <div
        ref={wrapRef}
        tabIndex={0}
        className="relative flex-1 min-h-0 overflow-hidden outline-none"
      >
        {/* Centered both ways; the page is always scaled to fit this pane. */}
        <div className="flex h-full w-full items-center justify-center p-4">

          <canvas
            ref={canvasRef}
            className="rounded-lg bg-background shadow-lg ring-1 ring-border"
          />
        </div>

        {/* Tap-to-page zones on the outer thirds of the pane. */}
        {total > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous page"
              onClick={() => goManual(pageRef.current - 1)}
              disabled={page <= 1}
              className="absolute inset-y-0 left-0 w-1/4 cursor-w-resize disabled:cursor-default"
            />
            <button
              type="button"
              aria-label="Next page"
              onClick={() => goManual(pageRef.current + 1)}
              disabled={!!total && page >= total}
              className="absolute inset-y-0 right-0 w-1/4 cursor-e-resize disabled:cursor-default"
            />
          </>
        )}

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted/40 pointer-events-none">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
    </div>
  );
});
